import React from 'react'
import { Box, Chip, Collapse, IconButton, Paper, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { SKY_GRADIENT } from '../social-media/design-tokens'
import { HERO_ACCENT } from './HeroStat'

export interface CollapsibleSectionProps {
  title: string
  /** Grey line beside the title. */
  subtitle?: string
  /** Leading icon, e.g. <TrendingUpIcon />. Sized by this component. */
  icon?: React.ReactNode
  /** Optional count pill after the title — omitted when 0 or undefined. */
  badge?: number
  expanded: boolean
  onToggle: () => void
  children: React.ReactNode
  'data-testid'?: string
}

/**
 * A titled section that opens and closes, with the gradient underline the
 * dashboards use as their section rule.
 *
 * The original page built this twice — "Trends Over Time" and "Comments" — as
 * two ~50-line blocks that had drifted into being byte-identical apart from
 * their icon, title and badge. Both carried the same `&::after` gradient trick
 * (a 4px bar absolutely positioned along the bottom edge, which is what gives
 * the underline its full-bleed width rather than the header's padded width).
 *
 * DEFERRED WORK IS THE CALLER'S JOB. This component does not know that opening
 * the comments section is what triggers its first fetch. It reports the toggle;
 * the page decides what that means. That is deliberate — a section whose
 * content is cheap should not pay for a fetch-on-open protocol it does not need.
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  subtitle,
  icon,
  badge,
  expanded,
  onToggle,
  children,
  'data-testid': testId,
}) => (
  <Paper
    elevation={0}
    data-testid={testId}
    sx={{
      borderRadius: 0,
      borderBottom: '1px solid',
      borderColor: 'rgba(0,0,0,0.06)',
      overflow: 'hidden',
    }}
  >
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        pt: 4.5,
        pb: 3,
        bgcolor: 'white',
        cursor: 'pointer',
        transition: 'background 0.15s',
        position: 'relative',
        '&:hover': { bgcolor: 'grey.50' },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: SKY_GRADIENT,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon && (
          <Box sx={{ display: 'flex', color: 'grey.600', '& > *': { fontSize: 20 } }}>{icon}</Box>
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'grey.800' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'grey.500' }}>
            {subtitle}
          </Typography>
        )}
        {Boolean(badge) && (
          <Chip
            label={(badge as number).toLocaleString()}
            size="small"
            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, bgcolor: HERO_ACCENT, color: 'white' }}
          />
        )}
      </Box>
      <IconButton size="small" sx={{ color: 'grey.500' }}>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Box>

    <Collapse in={expanded}>
      <Box sx={{ bgcolor: 'white' }}>{children}</Box>
    </Collapse>
  </Paper>
)

export default CollapsibleSection
